/** The Coverage tab's grid: every expected stream against every month of the reporting window.
 *
 * The question the Sources screen answers is *"what has not arrived, and whose is it to chase"*, and
 * a list of rows answers it one stream at a time. A grid answers it at a glance: a column of hatched
 * cells is a month nothing reached us for, a row of them is one supply that has gone quiet.
 *
 * Five decisions a tidy-up would undo without looking like it.
 *
 * **Six states, none collapsed, and each says so three ways** (F-040). A fill *and* a pattern *and*
 * a distinct glyph, with the word in the cell's accessible name and in the legend. `missing` is
 * hatched, `unknown` is dotted, `pending` is dashed, `not expected` is hollow, `submitted` and
 * `declared nil` are solid - so a printed copy in black and white, or a reader who cannot separate
 * the hues, still tells a gap from a declared zero. Pending is never drawn as missing: a window that
 * has not closed is the census's own distinction, and an on-event stream with nothing in it is
 * *unknown*, because nothing arriving may mean nothing happened.
 *
 * **A window is as wide as its months.** A quarterly stream's cell spans three columns and an annual
 * one twelve, so the grid never invents twelve states for a source that was judged four times.
 *
 * **A cell opens the existing flows, it does not rebuild them.** Selecting one explains it (what the
 * state means, what the person said, why a window was excused) and offers the *same* declare-nil,
 * take-back and open-the-stream actions the register has. Those forms live under the stream's own
 * row, so the actions here hand over to that row rather than growing a second copy of a form that
 * asks for a reason.
 *
 * **One tab stop, arrow keys inside** (the grid pattern). Four hundred cells as four hundred tab
 * stops is a screen a keyboard reader leaves.
 *
 * **The table is the same facts, not a summary.** Every window of every listed stream, in words, for
 * a reader who wants to sort, copy or listen to it rather than scan a picture.
 */

import { Hourglass, Table as TableIcon, SquaresFour } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { KeyboardEvent } from "react";
import { useMemo, useState } from "react";

import type { CoverageRollup, WaitingDocument } from "../api/coverageRollup";
import type { Coverage, SourceCoverage, SourceWindow } from "../api/sources";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { shortDate } from "../lib/format";
import { LIFE_BASIS, MONTHS, STATE, nameOf, period } from "./sourceModel";
import { StateGlyph, SupplyLine } from "./sourceParts";
import { CellPath } from "./coverage/CellPath";
import { waitingByWindow, windowKey } from "./coverage/waiting";
import { keepSearch } from "./search";
import heat from "./SourcesHeatmap.module.css";

/** How many streams the grid draws before the reader asks for more, and how many months it will
 * lay out. Both are said on screen when they bite; the table lists every window regardless. */
const STREAM_CEILING = 40;
const MONTH_CEILING = 36;

/** The six, in the order a reader should meet them: the open ones, then the resolved, then the
 * excused. The same six keys `STATE` holds - the legend is derived from it, never retyped. */
const ORDER = ["missing", "unknown", "pending", "submitted", "declared_nil", "not_expected"] as const;

const monthNumber = (iso: string): number => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;

interface Placed {
  window: SourceWindow;
  /** Zero-based first and last month column this window covers. */
  start: number;
  end: number;
  key: string;
}
interface Line {
  source: SourceCoverage;
  cells: Placed[];
}

/** Lay one stream's windows on the month axis. Ordered by date and clamped into the axis, so a
 * window is never dropped for starting before the first column or running past the last; a window
 * that would land on its neighbour's months starts after them. */
function place(source: SourceCoverage, months: number, firstMonth: number): Placed[] {
  const ordered = [...source.windows].sort((a, b) => a.period_start.localeCompare(b.period_start));
  const placed: Placed[] = [];
  let taken = -1;
  for (const window of ordered) {
    let start = Math.min(Math.max(monthNumber(window.period_start) - firstMonth, 0), months - 1);
    let end = Math.min(Math.max(monthNumber(window.period_end) - firstMonth, start), months - 1);
    if (start <= taken) start = Math.min(taken + 1, months - 1);
    if (end < start) end = start;
    taken = end;
    placed.push({ window, start, end, key: `${source.source_id}|${window.period_start}` });
  }
  return placed;
}

const stateWords = (state: string): string => STATE[state]?.short ?? state.replace(/_/g, " ");

/** What a cell is called, for a screen reader and the tooltip: which stream, which period, which of
 * the six states and what that means. The word is in the name so the state is never colour or
 * shape alone. */
function labelOf(source: SourceCoverage, window: SourceWindow, waiting = false): string {
  return `${nameOf(source)}, ${period(window, source.cadence)} (${shortDate(window.period_start)} – ${shortDate(
    window.period_end,
  )}): ${stateWords(window.state)} — ${STATE[window.state]?.long ?? ""}${
    waiting ? `. ${WAITING_WORDS}` : ""
  }`;
}

/** The annotation in the cell's own name and in the table: said as a note, in the ADR's words. The
 * state word before it is unchanged, and this never says "received". */
const WAITING_WORDS =
  "A document may cover it and is waiting for review; it does not count until verified";

/** Why a window was excused, when the stream's life ended and a window falls after it. Said in the
 * life-basis words the register uses, with where the remedy is. */
function lifeSentence(source: SourceCoverage, window: SourceWindow): string | null {
  if (window.state !== "not_expected" || !source.life_to || window.period_start <= source.life_to) return null;
  const basis = LIFE_BASIS[source.life_to_basis ?? ""];
  return `Nothing expected after ${shortDate(source.life_to)} — ${basis?.said ?? source.life_to_basis}.${
    basis ? ` ${basis.remedy.charAt(0).toUpperCase()}${basis.remedy.slice(1)}.` : ""
  }`;
}

export function StateSwatch({ state, children }: { state: string; children?: string }) {
  return (
    <span className={`${heat.cell} ${heat[state] ?? ""}`}>
      <span className={heat.face}>
        <StateGlyph state={state} />
        {children}
      </span>
    </span>
  );
}

export function CoverageLegend() {
  return (
    <div className={heat.legend} data-testid="sources-state-legend">
      {ORDER.map((key) => (
        <span key={key} className={heat.legendItem}>
          <StateSwatch state={key}>{STATE[key]?.short}</StateSwatch>
          <span className={heat.legendText}>{STATE[key]?.long}</span>
        </span>
      ))}
    </div>
  );
}

/** What the grid knows about documents waiting beside its windows (`GET /coverage`, ADR-0166). It
 * is read separately from the census and a failure of it never hides the census: the grid is still
 * the six states, and the gap in what it knows is said in words. */
export interface WaitingRead {
  rollup: CoverageRollup | undefined;
  pending: boolean;
  error: unknown;
  onRetry: () => void;
}

export function SourcesHeatmap({
  data,
  waiting,
  mayDeclare,
  onOpenStream,
  onDeclare,
  onWithdraw,
}: {
  data: Coverage;
  /** Absent in a caller that does not read `GET /coverage`: the grid then shows no annotation and
   * says nothing about one, which is the state before ADR-0166 and is not a claim. */
  waiting?: WaitingRead;
  mayDeclare: boolean;
  /** Put the reader on the stream's own row (the Sources tab), where its actions and forms are. */
  onOpenStream: (source: SourceCoverage) => void;
  onDeclare: (source: SourceCoverage, window: SourceWindow) => void;
  onWithdraw: (source: SourceCoverage, window: SourceWindow) => void;
}) {
  const [view, setView] = useState<"grid" | "table">("grid");
  const [only, setOnly] = useState<"all" | "gaps">("all");
  const [ceiling, setCeiling] = useState(STREAM_CEILING);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const firstMonth = monthNumber(data.period_start);
  const wanted = monthNumber(data.period_end) - firstMonth + 1;
  const months = Math.min(Math.max(wanted, 1), MONTH_CEILING);

  const byWindow = useMemo(() => waitingByWindow(waiting?.rollup), [waiting?.rollup]);
  const waitingOf = (source: SourceCoverage, window: SourceWindow): readonly WaitingDocument[] =>
    byWindow.get(windowKey(source.source_id, window.period_start)) ?? [];

  const hasGap = (source: SourceCoverage) => source.windows.some((w) => w.state === "missing");
  const gaps = data.sources.filter(hasGap).length;
  /** Streams with a gap come first, most missing first: the reader is here to find them. The sort is
   * stable, so the rest keep the census's own order. */
  const ordered = useMemo(
    () => [...data.sources].sort((a, b) => b.missing - a.missing),
    [data.sources],
  );
  const listed = only === "gaps" ? ordered.filter(hasGap) : ordered;
  const lines: Line[] = useMemo(
    () => listed.slice(0, ceiling).map((source) => ({ source, cells: place(source, months, firstMonth) })),
    [listed, ceiling, months, firstMonth],
  );

  const firstKey = lines[0]?.cells[0]?.key ?? null;
  const known = new Set(lines.flatMap((line) => line.cells.map((cell) => cell.key)));
  const active = focusKey && known.has(focusKey) ? focusKey : firstKey;
  const selected = (() => {
    for (const line of lines)
      for (const cell of line.cells)
        if (cell.key === selectedKey) return { source: line.source, window: cell.window };
    return null;
  })();

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-heat-cell]");
    if (!target) return;
    const row = Number(target.dataset.row);
    const at = Number(target.dataset.idx);
    const here = lines[row]?.cells[at];
    if (!here) return;
    let next: Placed | undefined;
    switch (event.key) {
      case "ArrowRight":
        next = lines[row]?.cells[at + 1];
        break;
      case "ArrowLeft":
        next = lines[row]?.cells[at - 1];
        break;
      case "Home":
        next = lines[row]?.cells[0];
        break;
      case "End":
        next = lines[row]?.cells[(lines[row]?.cells.length ?? 1) - 1];
        break;
      case "ArrowDown":
      case "ArrowUp": {
        const other = lines[row + (event.key === "ArrowDown" ? 1 : -1)];
        // The window in the neighbouring stream that covers this window's first month, else the
        // nearest one by start, so a quarterly row under a monthly one still has somewhere to land.
        next =
          other?.cells.find((cell) => cell.start <= here.start && here.start <= cell.end) ??
          [...(other?.cells ?? [])].sort(
            (a, b) => Math.abs(a.start - here.start) - Math.abs(b.start - here.start),
          )[0];
        break;
      }
      default:
        return;
    }
    event.preventDefault();
    if (!next) return;
    setFocusKey(next.key);
    document.getElementById(`heat-${next.key}`)?.focus();
  }

  const head = (
    <div className={heat.toolbar}>
      <div className={heat.group} role="group" aria-label="Which streams to show">
        {(
          [
            ["all", "All streams", data.sources.length],
            ["gaps", "With a gap", gaps],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            className={heat.toggle}
            aria-pressed={only === key}
            onClick={() => {
              setOnly(key);
              setCeiling(STREAM_CEILING);
            }}
          >
            {label} <span className={heat.count}>{count}</span>
          </button>
        ))}
      </div>
      <div className={heat.group} role="group" aria-label="Coverage view">
        <button
          type="button"
          className={heat.toggle}
          aria-pressed={view === "grid"}
          onClick={() => setView("grid")}
        >
          <SquaresFour size={14} aria-hidden="true" /> Grid
        </button>
        <button
          type="button"
          className={heat.toggle}
          aria-pressed={view === "table"}
          onClick={() => setView("table")}
        >
          <TableIcon size={14} aria-hidden="true" /> Table
        </button>
      </div>
    </div>
  );

  return (
    <Panel
      title="Coverage by month"
      sub="Each expected stream against each month of the reporting window"
      column="1 / 13"
    >
      <CoverageLegend />
      <WaitingStatus waiting={waiting} marked={byWindow.size} />
      {data.sources.length === 0 ? (
        <Empty>
          No sources have been declared, so there is nothing to lay out and completeness is{" "}
          <strong>unknown</strong>. Declare one on the Sources tab.
        </Empty>
      ) : (
        <>
          {head}
          {listed.length === 0 ? (
            <Empty>No stream has a missing bill in this reporting period.</Empty>
          ) : view === "grid" ? (
            <div className={heat.scroll} data-testid="sources-heatmap">
              <div
                className={heat.grid}
                role="grid"
                aria-label="Expected streams by month. Arrow keys move between periods."
                aria-rowcount={lines.length + 1}
                style={{ "--n": months } as React.CSSProperties}
                onKeyDown={onKeyDown}
              >
                <div className={heat.row} role="row">
                  <span className={heat.corner} role="columnheader">
                    Stream
                  </span>
                  {Array.from({ length: months }, (_, i) => {
                    const month = (firstMonth + i) % 12;
                    const year = Math.floor((firstMonth + i) / 12);
                    return (
                      <span
                        key={i}
                        className={heat.month}
                        role="columnheader"
                        style={{ gridColumn: i + 2, gridRow: 1 }}
                      >
                        {MONTHS[month]}
                        {i === 0 || month === 0 ? <span className={heat.year}>{year}</span> : null}
                      </span>
                    );
                  })}
                </div>
                {lines.map((line, row) => (
                  <div key={line.source.source_id} className={heat.row} role="row">
                    <span className={heat.name} role="rowheader" style={{ gridColumn: 1, gridRow: 1 }}>
                      <span className={heat.streamName}>{nameOf(line.source)}</span>
                      <span className={heat.streamMeta}>
                        {line.source.facility_name} · {line.source.cadence.replace(/_/g, "-")}
                      </span>
                    </span>
                    {line.cells.map((cell, idx) => {
                      const span = cell.end - cell.start + 1;
                      const state = cell.window.state;
                      const marked = waitingOf(line.source, cell.window).length > 0;
                      return (
                        <span
                          key={cell.key}
                          role="gridcell"
                          className={heat.slot}
                          style={{ gridColumn: `${cell.start + 2} / span ${span}`, gridRow: 1 }}
                        >
                          <button
                            type="button"
                            id={`heat-${cell.key}`}
                            data-heat-cell=""
                            data-state={state}
                            data-waiting={marked ? "yes" : undefined}
                            data-row={row}
                            data-idx={idx}
                            className={`${heat.cell} ${heat[state] ?? ""} ${
                              selectedKey === cell.key ? heat.picked : ""
                            }`}
                            tabIndex={cell.key === active ? 0 : -1}
                            aria-pressed={selectedKey === cell.key}
                            aria-label={labelOf(line.source, cell.window, marked)}
                            title={labelOf(line.source, cell.window, marked)}
                            onFocus={() => setFocusKey(cell.key)}
                            onClick={() => setSelectedKey(cell.key)}
                          >
                            <span className={heat.face}>
                              <StateGlyph state={state} />
                              {span >= 3 ? STATE[state]?.short : null}
                            </span>
                            {marked ? (
                              <Hourglass
                                size={10}
                                weight="bold"
                                aria-hidden="true"
                                data-waiting-mark=""
                                className={heat.waitMark}
                              />
                            ) : null}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={heat.scroll}>
              <table className={heat.table} data-testid="sources-heat-table">
                <caption className="visuallyHidden">
                  Every period of every listed stream, with its state in words
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Stream</th>
                    <th scope="col">Window</th>
                    <th scope="col">State</th>
                    <th scope="col">Note</th>
                    <th scope="col">
                      <span className="visuallyHidden">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.flatMap((line) =>
                    line.cells.map((cell) => (
                      <tr
                        key={cell.key}
                        data-state={cell.window.state}
                        data-waiting={waitingOf(line.source, cell.window).length > 0 ? "yes" : undefined}
                      >
                        <th scope="row">
                          {nameOf(line.source)}
                          <span className={heat.streamMeta}>{line.source.facility_name}</span>
                        </th>
                        <td>
                          {period(cell.window, line.source.cadence)} · {shortDate(cell.window.period_start)} –{" "}
                          {shortDate(cell.window.period_end)}
                        </td>
                        <td>
                          <StateSwatch state={cell.window.state}>{STATE[cell.window.state]?.short}</StateSwatch>
                        </td>
                        <td className={heat.noteCell}>
                          {cell.window.nil_reason
                            ? `“${cell.window.nil_reason}”`
                            : (lifeSentence(line.source, cell.window) ?? "")}
                          {waitingOf(line.source, cell.window).length > 0 ? (
                            <span className={heat.waitWords}>{WAITING_WORDS}.</span>
                          ) : null}
                        </td>
                        <td>
                          <button type="button" className={heat.open} onClick={() => onOpenStream(line.source)}>
                            Open
                            <span className="visuallyHidden"> {nameOf(line.source)}</span>
                          </button>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          )}
          {listed.length > ceiling ? (
            <p className={heat.ceiling} data-testid="sources-heat-ceiling">
              Showing {ceiling} of {listed.length} streams, those with the most missing bills first.{" "}
              <button type="button" className={heat.open} onClick={() => setCeiling(listed.length)}>
                Show all {listed.length}
              </button>
            </p>
          ) : null}
          {wanted > MONTH_CEILING ? (
            <p className={heat.ceiling}>
              The period is {wanted} months; the grid lays out the first {MONTH_CEILING}. The table
              lists every window.
            </p>
          ) : null}

          {view === "grid" ? (
            <section
              className={heat.detail}
              aria-label="Selected period"
              aria-live="polite"
              data-testid="sources-heat-detail"
            >
              {selected ? (
                <Detail
                  source={selected.source}
                  window={selected.window}
                  waiting={waitingOf(selected.source, selected.window)}
                  holdDays={waiting?.rollup?.gap_reminder_hold_days}
                  mayDeclare={mayDeclare}
                  onOpenStream={onOpenStream}
                  onDeclare={onDeclare}
                  onWithdraw={onWithdraw}
                />
              ) : (
                <p className={heat.hint}>
                  Select a period to read what its state means and what can be done about it.
                </p>
              )}
            </section>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/** What the screen knows, and does not, about documents waiting for review. Three honest states and
 * no silent one: still reading, could not read (with a retry), and read - in which case the mark's
 * key when any window carries it and the count that could not be placed anywhere. */
function WaitingStatus({ waiting, marked }: { waiting: WaitingRead | undefined; marked: number }) {
  if (!waiting) return null;
  if (waiting.error) {
    return (
      <ReadFailed of="The documents waiting for review" error={waiting.error} onRetry={waiting.onRetry} />
    );
  }
  if (waiting.pending || !waiting.rollup) {
    return (
      <p className={heat.waitStatus} role="status" data-testid="sources-heat-waiting-pending">
        Checking for documents waiting for review
      </p>
    );
  }
  const unplaced = waiting.rollup.unplaced_documents;
  if (marked === 0 && unplaced === 0) return null;
  return (
    <div className={heat.waitStatus} data-testid="sources-heat-waiting" data-marked={marked} data-unplaced={unplaced}>
      {marked > 0 ? (
        <p className={heat.waitLine}>
          <Hourglass size={12} weight="bold" aria-hidden="true" /> marks a period a document may cover while it
          waits for review. It is a note, not a state, and moves no count.
        </p>
      ) : null}
      {unplaced > 0 ? (
        <p className={heat.waitLine} data-testid="sources-heat-unplaced">
          {unplaced === 1
            ? "1 document waiting for review could not be placed on a period"
            : `${unplaced} documents waiting for review could not be placed on a period`}{" "}
          (no site stated, or a kind no stream expects). {unplaced === 1 ? "It is" : "They are"} not counted.{" "}
          <Link className={heat.open} to="/evidence" search={keepSearch}>
            Open the review queue
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function Detail({
  source,
  window,
  waiting,
  holdDays,
  mayDeclare,
  onOpenStream,
  onDeclare,
  onWithdraw,
}: {
  source: SourceCoverage;
  window: SourceWindow;
  waiting: readonly WaitingDocument[];
  holdDays?: number;
  mayDeclare: boolean;
  onOpenStream: (source: SourceCoverage) => void;
  onDeclare: (source: SourceCoverage, window: SourceWindow) => void;
  onWithdraw: (source: SourceCoverage, window: SourceWindow) => void;
}) {
  const life = lifeSentence(source, window);
  return (
    <>
      <div className={heat.detailHead}>
        <StateSwatch state={window.state}>{STATE[window.state]?.short}</StateSwatch>
        <span className={heat.detailTitle}>
          {nameOf(source)}
          <span className={heat.streamMeta}>
            {source.facility_name} · {period(window, source.cadence)} · {shortDate(window.period_start)} –{" "}
            {shortDate(window.period_end)}
          </span>
        </span>
      </div>
      <p className={heat.detailText}>{STATE[window.state]?.long}.</p>
      {window.nil_reason ? (
        <p className={heat.detailText}>It said: &ldquo;{window.nil_reason}&rdquo;</p>
      ) : null}
      {life ? <p className={heat.detailText}>{life}</p> : null}
      <SupplyLine source={source} />
      <CellPath source={source} window={window} waiting={waiting} holdDays={holdDays} />
      <div className={heat.actions}>
        {window.state === "missing" && mayDeclare ? (
          <button type="button" className={heat.primary} onClick={() => onDeclare(source, window)}>
            Confirm none for this period
          </button>
        ) : null}
        {window.state === "declared_nil" && mayDeclare && window.nil_declaration_id ? (
          <button type="button" className={heat.open} onClick={() => onWithdraw(source, window)}>
            Take back this confirmation
          </button>
        ) : null}
        <button type="button" className={heat.open} onClick={() => onOpenStream(source)}>
          Open this stream
        </button>
      </div>
      {window.state === "missing" && !mayDeclare ? (
        <p className={heat.detailText}>
          Either the data is still to arrive or nothing happened. Recording the second is an Approver&rsquo;s
          decision; the first is for whoever owns this stream to chase.
        </p>
      ) : null}
    </>
  );
}
